import { fullNameKey } from "odyssey-shared";

/**
 * The recent whisperers a reply reaches (ADR-068's amendment "chat commands, Discord style", item 4): the players
 * whose whispers the page received, newest first. The page alone holds them, in the chat store's memory beside the
 * log, so a reconnect keeps them and a reload empties them; nothing is kept in the browser's storage or sent to the
 * server. The list is bounded twice over, at 5 players and at 10 minutes after each one's last whisper, and a
 * family on the block list is passed over whenever the list is read, so lifting the block brings it back.
 */

/**
 * The most players the list holds.
 */
export const RECENT_WHISPERERS = 5;

/**
 * How long a player stays on the list after its last whisper, in milliseconds.
 */
export const RECENT_WHISPER_MS = 600_000;

/**
 * A player who whispered, as its last whisper named it.
 */
export interface Whisperer {
  readonly firstName: string;
  readonly familyName: string;
}

/**
 * One entry: the player, its lookup key and when it last whispered.
 */
interface Heard {
  readonly key: string;
  readonly name: Whisperer;
  readonly at: number;
}

/**
 * The players who whispered recently, newest first.
 *
 * @example
 * ```ts
 * const recent = new RecentWhisperers();
 * recent.heard("Mira", "Thornwood", Date.now());
 * recent.list(Date.now(), []);
 * // [{ firstName: "Mira", familyName: "Thornwood" }]
 * ```
 */
export class RecentWhisperers {
  private entries: readonly Heard[] = [];

  /**
   * Notes a whisper received: its sender moves to the front, named as this whisper named it, and the oldest past
   * `RECENT_WHISPERERS` leaves.
   *
   * @param firstName The sender's first name.
   * @param familyName The sender's family name.
   * @param now The page's clock.
   */
  public heard(firstName: string, familyName: string, now: number): void {
    const key = fullNameKey(firstName, familyName);
    this.entries = [{ key, name: { firstName, familyName }, at: now }, ...this.entries.filter((entry) => entry.key !== key)].slice(0, RECENT_WHISPERERS);
  }

  /**
   * Forgets every whisperer, as a change of character must: a reply never reaches a player who whispered someone
   * else.
   */
  public clear(): void {
    this.entries = [];
  }

  /**
   * The players who whispered within `RECENT_WHISPER_MS`, newest first, those of a blocked family passed over.
   *
   * @param now The page's clock.
   * @param blocks The families on the block list, as the gateway sent them.
   * @returns The whisperers.
   */
  public list(now: number, blocks: readonly string[]): readonly Whisperer[] {
    const blocked = new Set(blocks.map((family) => family.toLowerCase()));
    return this.entries.filter((entry) => now - entry.at < RECENT_WHISPER_MS && !blocked.has(entry.name.familyName.toLowerCase())).map((entry) => entry.name);
  }
}
